import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import {
  compareRecipeVersions,
  createRecipeDraft,
  getRecipeVersion,
  listRecipeVersions,
  RecipeVersionApiError,
  restoreRecipeVersion,
  snapshotRecipeVersion,
} from '../../services/recipeVersionService';
import type { RecipeDiffRow, RecipeSnapshot, RecipeVersionList } from '../../types/recipeVersion.types';
import { formatDateTime } from '../../utils/productionFormat';
import VersionCompareDialog from './VersionCompareDialog';
import VersionPreviewDialog from './VersionPreviewDialog';

interface RecipeVersionsPanelProps {
  recipeId: string;
  readOnly: boolean;
  onChanged: () => void;
  onDraftCreated: () => void;
  onLiveChanged: () => void;
}

const RecipeVersionsPanel: React.FC<RecipeVersionsPanelProps> = ({
  recipeId,
  readOnly,
  onChanged,
  onDraftCreated,
  onLiveChanged,
}) => {
  const { t } = useTranslation('production');
  const [list, setList] = useState<RecipeVersionList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [preview, setPreview] = useState<RecipeSnapshot | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [compareRows, setCompareRows] = useState<RecipeDiffRow[] | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setList(await listRecipeVersions(recipeId));
    } catch (err) {
      setError(err instanceof RecipeVersionApiError ? err.message : t('recipeVersions.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // recipeId değişince liste yenilenir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  const chart = useMemo(
    () => (list?.versions || [])
      .filter((row) => row.versionNo != null && row.standardUnitCost != null)
      .map((row) => ({ versionNo: row.versionNo as number, unitCost: row.standardUnitCost as number }))
      .sort((a, b) => a.versionNo - b.versionNo),
    [list]
  );

  const run = async (action: () => Promise<unknown>, live = false) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      onChanged();
      if (live) onLiveChanged();
    } catch (err) {
      setError(err instanceof RecipeVersionApiError ? err.message : t('recipeVersions.actionFailed'));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (versionNo: number) => {
    setSelected((current) => {
      if (current.includes(versionNo)) return current.filter((value) => value !== versionNo);
      if (current.length >= 2) return [current[1], versionNo];
      return [...current, versionNo];
    });
  };

  const openPreview = async (versionNo: number) => {
    await run(async () => {
      const detail = await getRecipeVersion(recipeId, versionNo);
      setPreview(detail.snapshot);
      setPreviewTitle(t('recipeVersions.previewTitle', { n: versionNo }));
    });
  };

  if (loading && !list) return <CircularProgress size={24} />;

  return (
    <Box>
      {list && (
        <Alert severity={list.hasUnversionedChanges ? 'warning' : 'success'} sx={{ mb: 1 }}>
          {list.hasUnversionedChanges
            ? t('recipeVersions.banner', { n: list.currentVersionNo })
            : t('recipeVersions.current', { n: list.currentVersionNo })}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {!readOnly && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button variant="outlined" disabled={busy} onClick={() => { setNote(''); setNoteOpen(true); }}>
            {t('recipeVersions.saveVersion')}
          </Button>
          <Button
            variant="outlined"
            disabled={busy || !!list?.draft}
            onClick={() => run(async () => {
              await createRecipeDraft(recipeId);
              onDraftCreated();
            })}
          >
            {t('recipeVersions.createDraft')}
          </Button>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          size="small"
          disabled={selected.length !== 2 || busy}
          onClick={() => run(async () => {
            setCompareRows(await compareRecipeVersions(recipeId, String(selected[0]), String(selected[1])));
          })}
        >
          {t('recipeVersions.compare')}
        </Button>
        <Button
          size="small"
          disabled={selected.length !== 1 || busy}
          onClick={() => run(async () => {
            setCompareRows(await compareRecipeVersions(recipeId, String(selected[0]), 'live'));
          })}
        >
          {t('recipeVersions.compareLive')}
        </Button>
      </Box>
      {(list?.versions || []).length === 0 ? (
        <Typography variant="body2">{t('recipeVersions.empty')}</Typography>
      ) : (
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { borderBottom: '1px solid', borderColor: 'divider', p: 0.75, textAlign: 'left' } }}>
          <thead>
            <tr>
              <th />
              <th>{t('recipeVersions.version')}</th>
              <th>{t('recipeVersions.createdAt')}</th>
              <th>{t('recipeVersions.user')}</th>
              <th>{t('recipeVersions.source')}</th>
              <th>{t('recipeVersions.note')}</th>
              <th>{t('recipeVersions.ingredients')}</th>
              <th>{t('recipeVersions.unitCost')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list!.versions.map((row) => (
              <tr key={row.versionNo}>
                <td>
                  <Checkbox
                    size="small"
                    checked={row.versionNo != null && selected.includes(row.versionNo)}
                    onChange={() => row.versionNo != null && toggle(row.versionNo)}
                  />
                </td>
                <td>v{row.versionNo}</td>
                <td>{formatDateTime(row.createdAt)}</td>
                <td>{row.createdBy || '—'}</td>
                <td>{t(`recipeVersions.sources.${row.source}`, { defaultValue: row.source })}</td>
                <td>{row.changeNote || '—'}</td>
                <td>{row.ingredientCount}</td>
                <td>{row.standardUnitCost ?? '—'}</td>
                <td>
                  <Button size="small" disabled={busy || row.versionNo == null} onClick={() => row.versionNo != null && void openPreview(row.versionNo)}>
                    {t('recipeVersions.view')}
                  </Button>
                  <Button
                    size="small"
                    disabled={busy || row.versionNo == null}
                    onClick={() => row.versionNo != null && run(async () => {
                      setCompareRows(await compareRecipeVersions(recipeId, String(row.versionNo), 'live'));
                    })}
                  >
                    {t('recipeVersions.compareLive')}
                  </Button>
                  {!readOnly && (
                    <>
                      <Button
                        size="small"
                        disabled={busy || row.versionNo == null || !!list?.draft}
                        onClick={() => row.versionNo != null && run(async () => {
                          await createRecipeDraft(recipeId, row.versionNo);
                          onDraftCreated();
                        })}
                      >
                        {t('recipeVersions.fromHere')}
                      </Button>
                      <Button
                        size="small"
                        disabled={busy || row.versionNo == null || !!list?.draft}
                        onClick={() => {
                          if (row.versionNo == null) return;
                          if (!window.confirm(t('recipeVersions.restoreConfirm', { n: row.versionNo }))) return;
                          void run(() => restoreRecipeVersion(recipeId, row.versionNo as number), true);
                        }}
                      >
                        {t('recipeVersions.restore')}
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Box>
      )}
      {chart.length > 0 && (
        <Box sx={{ height: 220, mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('recipeVersions.chart')}</Typography>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="versionNo" />
              <YAxis />
              <ChartTooltip />
              <Line type="monotone" dataKey="unitCost" stroke="#1976d2" dot />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
      <Dialog open={noteOpen} onClose={() => setNoteOpen(false)}>
        <DialogTitle>{t('recipeVersions.saveVersion')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t('recipeVersions.note')}
            placeholder={t('recipeVersions.notePlaceholder')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteOpen(false)}>{t('recipeVersions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => {
              setNoteOpen(false);
              void run(() => snapshotRecipeVersion(recipeId, note));
            }}
          >
            {t('recipeVersions.saveVersion')}
          </Button>
        </DialogActions>
      </Dialog>
      <VersionPreviewDialog open={!!preview} title={previewTitle} snapshot={preview} onClose={() => setPreview(null)} />
      <VersionCompareDialog open={compareRows != null} rows={compareRows || []} onClose={() => setCompareRows(null)} />
    </Box>
  );
};

export default RecipeVersionsPanel;
